import { ChangeDetectorRef, Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Router } from "@angular/router";
import { AuthenticatorMachineOptions, SocialProvider } from "@aws-amplify/ui";
import { AuthenticatorService, translations } from "@aws-amplify/ui-angular";
import { AccountService } from "../../../services/account.service";
import { AuthLocaleService } from "../../../services/auth-locale.service";
import { SignUpService } from "../../../services/sign-up.service";
import { LoadingService } from "../../../services/loading.service";
import { Hub, I18n } from 'aws-amplify/utils';
import { TranslateService } from "@ngx-translate/core";
import { take } from "rxjs";
import { DialogService } from "../../../services/dialog.service";
import { GoogleLinkService } from "../../../services/google-link.service";
import { SocialSignInService } from "../../../services/social-sign-in.service";
import {
  ContractLinkModalComponent
} from "../../../components/contract-link-modal/contract-link-modal.component";
import {
  CodeEmailHelpModalComponent,
  CodeEmailHelpModalData
} from "../../../components/code-email-help-modal/code-email-help-modal.component";
import {
  GOOGLE_LINK_ERROR_MESSAGE_KEYS,
  mailboxSearchQuery,
  mailProviderOf,
  SOCIAL_SIGN_IN_MESSAGE_KEYS,
  SocialSignInRefusal
} from "../../../models";

@Component({
  selector: 'cpf-amplify-login',
  templateUrl: './amplify-login.component.html',
  styleUrls: ['./amplify-login.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AmplifyLoginComponent implements OnInit, OnDestroy {
  signUpFields: AuthenticatorMachineOptions['signUpAttributes'] = ['email'];
  // Renders the "Sign In with Google" button. Cognito's Hosted UI carries the
  // redirect from here; the app client and `aws-exports` oauth block have to
  // list Google for it to resolve.
  socialProviders: SocialProvider[] = ['google'];
  authStatus: string = 'unauthenticated';
  langs = ['de', 'en', 'fr', 'it'];
  currentLang = 'de';
  /**
   * The banner above the authenticator. It carries what happened before this
   * page load: a refused Google sign-in, or the return of the contract-email
   * flow, which ends with one more press of the Google button.
   */
  signInMessage: { kind: 'success' | 'error', key: string } | null = null;
  /**
   * Set once a sign-in was refused with `error.social_link_required`. It keeps
   * the way back to the modal on the screen: closing it would otherwise leave
   * the customer with a whole Google sign-in to repeat.
   */
  contractLinkOffered = false;
  public formFields = {
    signUp: {
      email: {
        order: 1
      },
      password: {
        order: 2
      },
      confirm_password: {
        order: 3
      }
    },
  }

  // Cognito sends the sign-up and password-reset emails in the language it is handed here.
  // Both resend buttons of the sign-up code - on the code screen, and the one a sign-in of an
  // unconfirmed account presses for the customer - go through `handleResendSignUpCode`.
  public services: AuthenticatorMachineOptions['services'] = {
    handleSignUp: input => this.trackRequest(this.signUpService.signUp(input)),
    handleConfirmSignUp: input => this.trackRequest(this.signUpService.confirmSignUp(input)),
    handleResendSignUpCode: input => this.authLocale.resendSignUpCode(input.username),
    handleForgotPassword: input => this.trackRequest(this.authLocale.resetPassword(input)),
  };

  /**
   * True while a form submit of ours waits for Cognito; the submit button then shows a
   * spinner. `authenticator.isPending` can't be used: the request runs in a child actor of
   * the machine, and the service refreshes its facade on root transitions only, so it
   * stays false the whole time.
   */
  requestPending = false;
  private pendingRequests = 0;

  renderAuthenticator = false;

  /**
   * Fragments of the messages Amplify reports for a trip the customer ended
   * themselves - the back button on the consent screen, or the consent screen
   * closed. Nothing went wrong, so the screen says nothing.
   */
  private static readonly CANCELLED_REDIRECT_MESSAGES = [
    'cancelled OAuth flow',
    'has been canceled'
  ];

  /**
   * The address of a sign-up that still waits for its code. The page then opens on
   * "Create account" with the address filled in, and a new sign-up sends a new code.
   */
  pendingSignUpEmail: string | null = null;
  initialState: AuthenticatorMachineOptions['initialState'] = 'signIn';

  /** Shown for a moment after the search word was copied on the enter-code screen. */
  searchQueryCopied = false;

  private contractLinkModalOpen = false;
  private lastRoute?: string;
  private searchQueryCopiedTimeout?: ReturnType<typeof setTimeout>;
  private stopListeningToHub?: () => void;

  constructor(private accountService: AccountService,
              public authenticator: AuthenticatorService,
              private authLocale: AuthLocaleService,
              private signUpService: SignUpService,
              private translate: TranslateService,
              private cdr: ChangeDetectorRef,
              private loadingService: LoadingService,
              private dialogService: DialogService,
              private googleLinkService: GoogleLinkService,
              private socialSignInService: SocialSignInService,
              public router: Router) {

  }

  ngOnInit() {
    this.currentLang = this.translate.getCurrentLang() || localStorage.getItem('lang') || 'de';

    this.pendingSignUpEmail = this.signUpService.pendingEmail;
    this.initialState = this.pendingSignUpEmail ? 'signUp' : 'signIn';

    I18n.putVocabularies(translations);
    this.applyAmplifyI18n(this.currentLang);
    this.initializeAuthenticator();

    this.handleGoogleCallback();
    this.handleSocialSignInRefusal();
    this.watchForRedirectFailure();

    this.authenticator.subscribe(async (authState) => {
      this.authStatus = authState.authStatus;
      this.watchForSignUpScreen(authState.route);

      if (authState.authStatus === 'authenticated') {
        this.signUpService.forgetPending();
        this.pendingSignUpEmail = null;
        // Cover the authenticator immediately: attribute fetch and the data
        // resolver both run before the target page can render.
        this.loadingService.bootstrapReset();
        try {
          await this.accountService.handleFetchUserAttributes();
          await this.router.navigate(['calingo', 'pet', 'invoice']);
        } catch (err) {
          console.error('Fetching user attributes failed', err);
        } finally {
          this.loadingService.bootstrapComplete();
        }
        return;
      }

      if (authState.authStatus === 'unauthenticated') {
        // The authenticator reports the same refusal codes the OAuth return
        // path carries, and in the same shape: inside a sentence. Whichever of
        // the two the refusal reaches us on, it is answered the same way.
        const refusal = this.socialSignInService.refusalIn(authState.error);

        if (refusal) {
          authState.error = this.translate.instant(this.refusalMessageKey(refusal));
        }

        if (refusal === 'social_link_required') {
          this.openContractLinkModal();
        }
      }
    });
  }

  setLang(lang: string): void {
    localStorage.setItem('lang', lang);
    localStorage.setItem('explicitly-set-lang', 'true');
    this.currentLang = lang;
    this.renderAuthenticator = false;
    this.cdr.detectChanges();
    this.translate.use(lang).pipe(take(1)).subscribe(() => {
      this.applyAmplifyI18n(lang);
    });
  }

  ngOnDestroy(): void {
    this.stopListeningToHub?.();
    clearTimeout(this.searchQueryCopiedTimeout);
  }

  /**
   * Hands our `services` to the authenticator machine when `<amplify-authenticator>`
   * will not.
   *
   * The machine is a root singleton that starts with the app. A sign-out while it
   * waits for its config (the customer logs out of the portal) takes it straight to
   * the sign-in screen with Amplify's default services. `<amplify-authenticator>`
   * sends its config only when it sees the `setup` route, so it never does, and
   * sign-up then skips the email normalisation and the locale.
   *
   * The machine takes the first config only and ignores this call otherwise, so
   * it has to carry exactly the inputs the template passes.
   */
  private initializeAuthenticator(): void {
    this.authenticator.initializeMachine({
      initialState: this.initialState,
      services: this.services,
      signUpAttributes: this.signUpFields,
      socialProviders: this.socialProviders,
      formFields: this.formFields,
    });
  }

  /**
   * Fills in the address of the pending sign-up each time the "Create account" form opens.
   * The authenticator takes no initial value for a field, and its sign-up form reads the
   * values from the inputs when it submits, so the input is filled the way typing fills it.
   */
  private watchForSignUpScreen(route: string): void {
    const opened = route === 'signUp' && this.lastRoute !== 'signUp';
    this.lastRoute = route;

    if (opened && this.pendingSignUpEmail) {
      this.prefillSignUpEmail(this.pendingSignUpEmail);
    }
  }

  /** The form renders after the route changes, so the input is looked for over a few frames. */
  private prefillSignUpEmail(email: string, framesLeft = 10): void {
    const input = document.querySelector<HTMLInputElement>('amplify-sign-up input[name="email"]');

    if (!input) {
      if (framesLeft > 0) {
        requestAnimationFrame(() => this.prefillSignUpEmail(email, framesLeft - 1));
      }
      return;
    }

    if (input.value) {
      return;
    }

    input.value = email;
    input.dispatchEvent(new Event('input', { bubbles: true }));
  }

  /**
   * Names a Google round trip that came back with a code Amplify could not
   * exchange - a state that no longer matches, or a token endpoint that
   * refused.
   *
   * Such a failure leaves the customer on this screen with `?code=` still in
   * the address bar and nothing to read. Only Amplify knows the reason, and it
   * reports it on the Hub, so it is put on the console for a support ticket.
   */
  private watchForRedirectFailure(): void {
    this.stopListeningToHub = Hub.listen('auth', ({ payload }) => {
      if (payload.event !== 'signInWithRedirect_failure') {
        return;
      }

      const message = (payload.data as { error?: Error } | undefined)?.error?.message;

      console.warn('Google sign-in could not be completed:', message ?? '(no reason given)');

      if (!this.shouldReportRedirectFailure(message)) {
        return;
      }

      this.signInMessage = { kind: 'error', key: 'auth.social-sign-in.errors.generic' };
      // The Hub fires from Amplify's own promise chain, which does not belong
      // to the change detection that drew this page.
      this.cdr.detectChanges();
    });
  }

  /**
   * A failure is only worth a message when nothing better answers it already.
   *
   *  - A refusal carries its own copy, and `social_link_required` carries the
   *    contract-email modal. Both are read off the URL, before this listener
   *    is even attached.
   *  - A trip the customer ended themselves is not a failure.
   */
  private shouldReportRedirectFailure(message: string | undefined): boolean {
    if (this.signInMessage || this.contractLinkModalOpen) {
      return false;
    }

    if (this.socialSignInService.refusalIn(message)) {
      return false;
    }

    return !AmplifyLoginComponent.CANCELLED_REDIRECT_MESSAGES
      .some(cancelled => message?.includes(cancelled));
  }

  /**
   * Answers a Google sign-in that Cognito refused on the OAuth return path.
   *
   * `social_link_required` is the one refusal the customer can act on: they may
   * hold a policy on another address, and the modal is how they say so.
   */
  private handleSocialSignInRefusal(): void {
    const refusal = this.socialSignInService.consumeRefusal();

    if (!refusal) {
      return;
    }

    // The description names the check that refused, which is what makes a
    // support ticket answerable. It never reaches the screen.
    console.warn('Google sign-in refused:', refusal.rawDescription ?? '(no description given)');

    if (refusal.refusal === 'social_link_required') {
      this.openContractLinkModal();
      return;
    }

    this.signInMessage = {
      kind: 'error',
      key: this.refusalMessageKey(refusal.refusal)
    };
  }

  /**
   * Answers the callback of the contract-email flow, which lands here because
   * the account it just built cannot be signed into by anybody but Cognito.
   */
  private handleGoogleCallback(): void {
    const result = this.googleLinkService.consumeCallbackResult();

    if (!result) {
      return;
    }

    if (result.status !== 'error') {
      this.signInMessage = { kind: 'success', key: 'auth.google-link.linked' };
      return;
    }

    console.warn('Google account link refused:', result.rawReason ?? '(no reason given)');

    const messageKey = result.reason ? GOOGLE_LINK_ERROR_MESSAGE_KEYS[result.reason] : 'generic';

    this.signInMessage = { kind: 'error', key: `auth.google-link.errors.${messageKey}` };
  }

  private refusalMessageKey(refusal?: SocialSignInRefusal): string {
    if (refusal === 'social_link_required') {
      return 'auth.social-sign-in.errors.link-required';
    }

    // A refusal we do not know, and the OAuth errors that are not refusals at
    // all - a cancelled consent screen, for one - share the catch-all copy.
    return (refusal && SOCIAL_SIGN_IN_MESSAGE_KEYS[refusal]) || 'auth.social-sign-in.errors.generic';
  }

  /**
   * What the enter-code screen tells the customer to search the mailbox for. The
   * authenticator holds the full address; the one in its delivery details is masked.
   */
  get codeEmailSearchQuery(): string {
    return mailboxSearchQuery(mailProviderOf(this.authenticator.username));
  }

  onSearchQueryCopied(): void {
    this.searchQueryCopied = true;
    clearTimeout(this.searchQueryCopiedTimeout);
    this.searchQueryCopiedTimeout = setTimeout(() => this.searchQueryCopied = false, 2000);
  }

  private trackRequest<T>(request: Promise<T>): Promise<T> {
    this.pendingRequests++;
    this.requestPending = true;
    return request.finally(() => {
      this.pendingRequests--;
      this.requestPending = this.pendingRequests > 0;
    });
  }

  openCodeEmailHelp(): void {
    const data: CodeEmailHelpModalData = { email: this.authenticator.username };

    this.dialogService.openDialog(CodeEmailHelpModalComponent, { data, disableClose: false });
  }

  openContractLinkModal(): void {
    if (this.contractLinkModalOpen) {
      return;
    }

    this.contractLinkModalOpen = true;
    this.contractLinkOffered = true;
    this.signInMessage = null;

    const dialogRef = this.dialogService.openDialog(ContractLinkModalComponent, {});

    dialogRef.afterClosed().subscribe(() => {
      this.contractLinkModalOpen = false;
      // Says why the sign-in was refused, now that the modal no longer says it.
      this.signInMessage = { kind: 'error', key: 'auth.social-sign-in.errors.link-required' };
    });
  }

  private applyAmplifyI18n(lang: string): void {
    this.translate.get([
      'auth.sign-in.email-placeholder',
      'auth.sign-in.password-placeholder',
      'auth.sign-up.password-confirm-placeholder',
      'auth.confirm-code.heading',
      'auth.confirm-code.sent-to',
      'auth.confirm-code.arrival',
      'auth.confirm-code.label',
      'auth.confirm-code.placeholder',
      'auth.confirm-code.submit',
      'auth.confirm-code.resend',
      'auth.forgot-password.link',
      'auth.forgot-password.heading',
      'auth.forgot-password.send-code',
      'auth.forgot-password.back-to-sign-in',
      'auth.forgot-password.code-label',
      'auth.forgot-password.code-placeholder',
      'auth.forgot-password.new-password',
      'auth.forgot-password.confirm-password',
      'auth.forgot-password.submit',
      'auth.sign-in.with-google',
      'auth.sign-up.with-google'
    ]).subscribe(translated => {
      I18n.putVocabulariesForLanguage(lang, {
        'Enter your Email': translated['auth.sign-in.email-placeholder'],
        'Enter your email': translated['auth.sign-in.email-placeholder'],
        'Enter your Password': translated['auth.sign-in.password-placeholder'],
        'Enter your password': translated['auth.sign-in.password-placeholder'],
        'Please confirm your Password': translated['auth.sign-up.password-confirm-placeholder'],
        // Code confirmation screens. Amplify builds the subtitle by concatenation:
        // `${sent-to} ${destination}. ${arrival}.` — so 'sent-to' has to end right
        // before the email address, and neither part carries its own punctuation.
        'We Emailed You': translated['auth.confirm-code.heading'],
        'Your code is on the way. To log in, enter the code we emailed to': translated['auth.confirm-code.sent-to'],
        'It may take a minute to arrive': translated['auth.confirm-code.arrival'],
        'Confirmation Code': translated['auth.confirm-code.label'],
        'Enter your code': translated['auth.confirm-code.placeholder'],
        'Confirm': translated['auth.confirm-code.submit'],
        'Resend Code': translated['auth.confirm-code.resend'],
        // Password reset. 'Reset Password' and 'Back to Sign In' are shared by the
        // request-code and set-new-password screens; the email field on the first
        // screen reuses the 'Enter your email' entry above.
        'Forgot your password?': translated['auth.forgot-password.link'],
        'Reset Password': translated['auth.forgot-password.heading'],
        'Send code': translated['auth.forgot-password.send-code'],
        'Back to Sign In': translated['auth.forgot-password.back-to-sign-in'],
        'Code *': translated['auth.forgot-password.code-label'],
        'Code': translated['auth.forgot-password.code-placeholder'],
        'New Password': translated['auth.forgot-password.new-password'],
        'Confirm Password': translated['auth.forgot-password.confirm-password'],
        'Submit': translated['auth.forgot-password.submit'],
        // Amplify labels the federated button differently per screen.
        'Sign In with Google': translated['auth.sign-in.with-google'],
        'Sign Up with Google': translated['auth.sign-up.with-google']
      });
      I18n.setLanguage(lang);
      this.renderAuthenticator = true;

      // A language switch renders the form again, with empty inputs.
      if (this.authenticator.route === 'signUp' && this.pendingSignUpEmail) {
        this.prefillSignUpEmail(this.pendingSignUpEmail);
      }
    });
  }
}
